import React, { useState, useEffect } from 'react';
import { Download, Printer, Search, FileText, Landmark } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { exportToPDF } from '@/utils/groupReportPDF';

const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

const GroupBalanceSheet = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedCollege, setSelectedCollege] = useState('');
  const [colleges, setColleges] = useState([]);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchColleges(); fetchReport(); }, []);

  const fetchColleges = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/groups/my-group`, { headers: { Authorization: `Bearer ${token}` } });
      setColleges(res.data.colleges || []);
    } catch (e) { console.error(e); }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/groups/my-group/reports/balance-sheet?as_of_date=${asOfDate}`;
      if (selectedCollege) url += `&college_id=${selectedCollege}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  const exportToExcel = () => {
    if (!data?.colleges?.length) return toast.error('No data to export');
    const ws = XLSX.utils.json_to_sheet(data.colleges.map(c => ({
      'College': c.college_name, 'Fixed Assets': c.total_fixed_assets, 'Bank Balance': c.bank_balance,
      'Cash Balance': c.cash_balance, 'Total Assets': c.total_assets, 'Liabilities': c.total_liabilities, 'Capital': c.capital
    })).concat([{
      'College': 'GROUP TOTAL', 'Fixed Assets': data.totals?.total_fixed_assets, 'Bank Balance': data.totals?.bank_balance,
      'Cash Balance': data.totals?.cash_balance, 'Total Assets': data.totals?.total_assets,
      'Liabilities': data.totals?.total_liabilities, 'Capital': data.totals?.capital
    }]));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Balance Sheet');
    XLSX.writeFile(wb, `Group_Balance_Sheet_${asOfDate}.xlsx`);
  };

  const exportPDF = () => {
    if (!data?.colleges?.length) return toast.error('No data to export');
    const rows = data.colleges.map(c => [c.college_name, fmt(c.total_fixed_assets), fmt(c.bank_balance), fmt(c.cash_balance), fmt(c.total_assets), fmt(c.total_liabilities), fmt(c.capital)]);
    rows.push(['GROUP TOTAL', fmt(data.totals?.total_fixed_assets), fmt(data.totals?.bank_balance), fmt(data.totals?.cash_balance), fmt(data.totals?.total_assets), fmt(data.totals?.total_liabilities), fmt(data.totals?.capital)]);
    exportToPDF('Group Balance Sheet',
      ['College', 'Fixed Assets', 'Bank', 'Cash', 'Total Assets', 'Liabilities', 'Capital'],
      rows,
      `Group_Balance_Sheet_${asOfDate}`,
      `As of: ${asOfDate} | Total Assets: ${fmt(data.totals?.total_assets)} | Liabilities: ${fmt(data.totals?.total_liabilities)} | Capital: ${fmt(data.totals?.capital)}`
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="group-balance-sheet">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Balance Sheet</h1>
          <p className="text-sm text-gray-500">Consolidated balance sheet across all colleges</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="group-bs-excel-btn" onClick={exportToExcel} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"><Download className="h-4 w-4" /> Excel</button>
          <button data-testid="group-bs-pdf-btn" onClick={exportPDF} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"><FileText className="h-4 w-4" /> PDF</button>
          <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"><Printer className="h-4 w-4" /> Print</button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-4 mb-6 flex flex-wrap gap-4 items-end">
        <div><label className="block text-xs font-medium text-gray-600 mb-1">As of Date</label><input data-testid="group-bs-as-of-date" type="date" value={asOfDate} onChange={e => setAsOfDate(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">College</label>
          <select data-testid="group-bs-college-filter" value={selectedCollege} onChange={e => setSelectedCollege(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
            <option value="">All Colleges</option>{colleges.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <button data-testid="group-bs-search-btn" onClick={fetchReport} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2"><Search className="h-4 w-4" /> Search</button>
      </div>

      {loading ? <div className="text-center py-12 text-gray-500">Loading...</div> : data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-blue-500">
              <div className="flex items-center gap-2"><Landmark className="h-4 w-4 text-blue-500" /><p className="text-xs text-gray-500">Total Assets (Group)</p></div>
              <p data-testid="group-bs-total-assets" className="text-2xl md:text-3xl font-bold text-blue-600">{fmt(data.totals?.total_assets)}</p>
              <p className="text-xs text-gray-400 mt-1">Fixed: {fmt(data.totals?.total_fixed_assets)} | Bank: {fmt(data.totals?.bank_balance)} | Cash: {fmt(data.totals?.cash_balance)}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-red-500">
              <p className="text-xs text-gray-500">Total Liabilities (Group)</p>
              <p data-testid="group-bs-total-liabilities" className="text-2xl md:text-3xl font-bold text-red-600">{fmt(data.totals?.total_liabilities)}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-emerald-500">
              <p className="text-xs text-gray-500">Capital (Assets − Liabilities)</p>
              <p data-testid="group-bs-capital" className="text-2xl md:text-3xl font-bold text-emerald-600">{fmt(data.totals?.capital)}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow">
            <div className="p-4 border-b"><h3 className="font-bold">College-wise Balance Sheet (as of {data.as_of_date})</h3></div>
            <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="bg-gray-50"><tr>
              <th className="text-left p-3">College</th><th className="text-right p-3">Fixed Assets</th><th className="text-right p-3">Bank</th><th className="text-right p-3">Cash</th><th className="text-right p-3">Total Assets</th><th className="text-right p-3">Liabilities</th><th className="text-right p-3">Capital</th>
            </tr></thead><tbody className="divide-y">
              {data.colleges?.map(c => (
                <tr key={c.college_id} className="hover:bg-gray-50" data-testid={`group-bs-row-${c.college_id}`}>
                  <td className="p-3 font-medium">{c.college_name}
                    {c.is_external && <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">🔗 External</span>}
                    {c.external_error && <span className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Unreachable</span>}
                  </td>
                  <td className="p-3 text-right">{fmt(c.total_fixed_assets)}</td>
                  <td className="p-3 text-right">{fmt(c.bank_balance)}</td>
                  <td className="p-3 text-right">{fmt(c.cash_balance)}</td>
                  <td className="p-3 text-right font-bold text-blue-700">{fmt(c.total_assets)}</td>
                  <td className="p-3 text-right text-red-700">{fmt(c.total_liabilities)}</td>
                  <td className="p-3 text-right font-bold text-emerald-700">{fmt(c.capital)}</td>
                </tr>
              ))}
              <tr className="bg-gray-100 font-bold">
                <td className="p-3">GROUP TOTAL</td>
                <td className="p-3 text-right">{fmt(data.totals?.total_fixed_assets)}</td>
                <td className="p-3 text-right">{fmt(data.totals?.bank_balance)}</td>
                <td className="p-3 text-right">{fmt(data.totals?.cash_balance)}</td>
                <td className="p-3 text-right text-blue-700">{fmt(data.totals?.total_assets)}</td>
                <td className="p-3 text-right text-red-700">{fmt(data.totals?.total_liabilities)}</td>
                <td className="p-3 text-right text-emerald-700">{fmt(data.totals?.capital)}</td>
              </tr>
            </tbody></table></div>
          </div>
        </>
      )}
    </div>
  );
};

export default GroupBalanceSheet;
